import type { FilterGroup } from "@/mocks/experienceCatalog";

interface Selected {
  audience: string[];
  season: string[];
  duration: string[];
}

interface ExperienceFilterBarProps {
  groups: FilterGroup[];
  selected: Selected;
  onToggle: (groupId: keyof Selected, optionId: string) => void;
  onClear: () => void;
  resultCount: number;
  totalCount: number;
}

export default function ExperienceFilterBar({
  groups,
  selected,
  onToggle,
  onClear,
  resultCount,
  totalCount,
}: ExperienceFilterBarProps) {
  const activeCount =
    selected.audience.length + selected.season.length + selected.duration.length;

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-7">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-equalizer-line text-xl"></i>
          </span>
          <div>
            <p className="font-heading text-lg text-foreground-950 font-semibold">
              Refine your search
            </p>
            <p className="text-sm text-foreground-600">
              Showing {resultCount} of {totalCount} experiences
            </p>
          </div>
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-700 hover:text-accent-800 transition-colors whitespace-nowrap cursor-pointer self-start sm:self-auto"
          >
            <i className="ri-close-circle-line"></i>
            Clear all filters ({activeCount})
          </button>
        )}
      </div>

      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.id}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 flex items-center justify-center text-secondary-600">
                <i className={group.icon}></i>
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground-500">
                {group.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => {
                const isActive = selected[group.id].includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onToggle(group.id, option.id)}
                    className={`inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                      isActive
                        ? "bg-primary-500 border-primary-500 text-background-50"
                        : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    <i className={`${option.icon} text-base`}></i>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}